import { Component, inject, signal } from '@angular/core'
import { Router, RouterModule } from '@angular/router'

import { IonicModule } from '@ionic/angular'
import { MenuController } from '@ionic/angular/standalone'

import { FeedbackService, ListFeedbackTypesResponseDTO } from '../../apiModule'
import { QuangAuthService } from 'quang/auth'

import { FeedbackArchiveControllerService } from '../../feedbacks/feedback-archive/store/feedback-archive-controller.service'
import { FeedbackHistoryControllerService } from '../../feedbacks/feedback-history/store/feedback-history-controller.service'

import { environment } from '../../../environments/environment'
import { OAuthService } from 'angular-oauth2-oidc'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { filter } from 'rxjs'

import packageInfo from '../../../../package.json'
import { TranslocoPipe } from '@jsverse/transloco'
import { NotificationService } from 'src/app/apiModule'
import { CounterController } from '../counter-store/counter-controller.service'
import { CounterComponent } from '../counter/counter.component'
import { UserService } from 'src/app/user.service'
import { SvgIconComponent } from 'angular-svg-icon'

@Component({
    selector: "pf-aside",
    templateUrl: "./aside.component.html",
    styleUrls: ["./aside.component.scss"],

  imports: [RouterModule, IonicModule, TranslocoPipe, CounterComponent, SvgIconComponent]
})
export class AsideComponent {
  private readonly authService = inject(QuangAuthService)
  private readonly feedbackService = inject(FeedbackService)
  private readonly menuController = inject(MenuController)
  private readonly notificationService = inject(NotificationService)
  private readonly router = inject(Router)
  private readonly oauthService = inject(OAuthService)
  private readonly archiveController = inject(FeedbackArchiveControllerService)
  private readonly historyController = inject(FeedbackHistoryControllerService)
  private readonly counterController = inject(CounterController)
  private readonly userService = inject(UserService)

  appVersion: string = packageInfo.version
  readonly feedbackTypes = signal<ListFeedbackTypesResponseDTO | null>(null)
  readonly user = this.userService.user

  readonly isManagerOrReporter = this.counterController.isManagerOrReporter
  readonly isServiceDeskOrAdmin = this.counterController.isServiceDeskOrAdmin
  readonly isAdministrator = this.counterController.isAdministrator

  constructor() {
    toObservable(this.userService.user)
      .pipe(
        filter((user) => !!user),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.getUserRoles()
      })
    this.getFeedbackTypes()
  }

  private getLocal(key: string): string | null {
    return localStorage.getItem(key)
  }

  private getSession<T = any>(key: string): T | null {
    const value = sessionStorage.getItem(key)
    if (!value) {
      return null
    }

    try {
      return JSON.parse(value) as T
    } catch {
      return null
    }
  }

  private setSession(key: string, value: unknown): void {
    sessionStorage.setItem(key, JSON.stringify(value))
  }

  getUserRoles(): void {
    const token = this.getLocal('push-notification-token')
    if (token) {
      this.setPushNotificationToken(token, false)
    }
  }

  getFeedbackTypes(): void {
    if (this.authService.isAuthenticated()) {
      const feedbackTypes = this.getSession<ListFeedbackTypesResponseDTO>('feedbackTypes')
      if (!feedbackTypes) {
        this.listFeedbackTypes()
      } else {
        this.feedbackTypes.set(feedbackTypes)
      }
    }
  }

  listFeedbackTypes(): void {
    this.feedbackService.listFeedbackTypes().subscribe((ft) => {
      this.feedbackTypes.set(ft)
      this.setSession('feedbackTypes', ft)
    });
  }

  goToHome(): void {
    this.navigate("/");
  }

  goToHistoryByType(feedbackType: string): void {
    this.historyController.updateSearch({ status: "OPEN", type: feedbackType });
    this.navigate("/feedbacks/history");
  }

  goToHistory(): void {
    this.historyController.updateSearch({ status: "OPEN" });
    this.navigate("/feedbacks/history");
  }

  goToArchive(): void {
    this.archiveController.updateSearch({ status: "CLOSED" });
    this.navigate("/feedbacks/archive");
  }

  goToHistoryToManager(): void {
    this.historyController.updateSearch({ status: "OPEN", managermanaged: true });
    this.navigate("/feedbacks/history");
  }

  goToArchiveToManager(): void {
    this.archiveController.updateSearch({ status: "CLOSED", managermanaged: true });
    this.navigate("/feedbacks/archive");
  }

  goToUnreadList(): void {
    this.historyController.updateSearch({
      status: "OPEN",
      onlyUnreadFeedback: true,
      onlyUnreadMessages: false,
    });
    this.navigate("/feedbacks/history");
  }

  navigate(page: string): void {
    this.router.navigate([page]);
    this.menuController.close();
  }

  goToUnreadMessagesList(): void {
    this.historyController.updateSearch({
      status: "OPEN",
      onlyUnreadFeedback: false,
      onlyUnreadMessages: true,
    });
    this.navigate("/feedbacks/history");
  }

  goToUnreadClosedMessagesList() {
    this.archiveController.updateSearch({
      status: "CLOSED",
      onlyUnreadFeedback: false,
      onlyUnreadMessages: true,
    });
    this.navigate("/feedbacks/archive");
  }

  logout (): void {
    this.setPushNotificationToken('', true)
  }

  setPushNotificationToken(token: string, loggingOut: boolean): void {
    console.log("setPushNotificationToken", {
      appName: "productFeedback",
      appVersion: environment.deviceInfo.appVersion,
      channelId: environment.deviceInfo.uuid,
      channelType: environment.deviceInfo.platform,
      channelToken: token,
      userId: this.user()?.username,
    });
    // Set/clear user device token for push notifications
    if (environment.isApp) {
      this.notificationService
        .registerDevice({
          appName: "productFeedback",
          appVersion: environment.deviceInfo.appVersion,
          channelId: environment.deviceInfo.uuid,
          channelType: environment.deviceInfo.platform,
          channelToken: token,
          userId: this.user()?.username,
        })
        .subscribe((_res) => {
          if (loggingOut) {
            this.authService.logout().finally(() => this.triggerLogin())
          }
        });
    } else {
      this.authService.logout()
    }
  }

  triggerLogin(): void {
    this.oauthService.logOut(true);
    this.oauthService.stopAutomaticRefresh();
    window.location.reload();
  }

}
